import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Database,
  RefreshCw,
  Trash2,
  Globe,
  Server,
  HardDrive,
  FileText,
  Layout,
  Menu,
  TrendingUp,
  Bot,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Film,
  Play,
  Tag,
  MapPin,
  Calendar,
  Search,
  Newspaper,
  User,
  Clapperboard,
  Map,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCacheStore } from '@/stores/cacheStore';
import {
  CacheType,
  ClientCacheType,
  CLIENT_CACHE_TYPE_LABELS,
  DYNAMIC_CLIENT_CACHE_TYPES,
} from '@/types/cache';

// Icon mapping cho các loại cache
const CACHE_TYPE_ICONS: Record<CacheType, React.ReactNode> = {
  [CacheType.ALL]: <Database className="h-5 w-5" />,
  [CacheType.SIDEBAR]: <Layout className="h-5 w-5" />,
  [CacheType.TRENDING]: <TrendingUp className="h-5 w-5" />,
  [CacheType.MENU]: <Menu className="h-5 w-5" />,
  [CacheType.SITEMAP]: <Globe className="h-5 w-5" />,
  [CacheType.PAGES]: <FileText className="h-5 w-5" />,
  [CacheType.BLOG_POSTS]: <FileText className="h-5 w-5" />,
  [CacheType.ARTICLES]: <FileText className="h-5 w-5" />,
  [CacheType.LAYOUT]: <Layout className="h-5 w-5" />,
  [CacheType.ROBOT]: <Bot className="h-5 w-5" />,
};

const CLIENT_CACHE_TYPE_ICONS: Record<ClientCacheType, React.ReactNode> = {
  // Existing
  [ClientCacheType.ALL]: <Globe className="h-5 w-5" />,
  [ClientCacheType.HOME]: <Layout className="h-5 w-5" />,
  [ClientCacheType.MOVIES]: <FileText className="h-5 w-5" />,
  [ClientCacheType.NEWS]: <Newspaper className="h-5 w-5" />,
  [ClientCacheType.TRENDING]: <TrendingUp className="h-5 w-5" />,
  [ClientCacheType.PAGES]: <FileText className="h-5 w-5" />,
  [ClientCacheType.LAYOUT]: <Layout className="h-5 w-5" />,
  // New types
  [ClientCacheType.MOVIE_DETAIL]: <Film className="h-5 w-5" />,
  [ClientCacheType.EPISODE]: <Play className="h-5 w-5" />,
  [ClientCacheType.CATEGORY]: <Tag className="h-5 w-5" />,
  [ClientCacheType.COUNTRY]: <MapPin className="h-5 w-5" />,
  [ClientCacheType.YEAR]: <Calendar className="h-5 w-5" />,
  [ClientCacheType.SEARCH]: <Search className="h-5 w-5" />,
  [ClientCacheType.NEWS_DETAIL]: <Newspaper className="h-5 w-5" />,
  [ClientCacheType.ACTOR]: <User className="h-5 w-5" />,
  [ClientCacheType.DIRECTOR]: <Clapperboard className="h-5 w-5" />,
  [ClientCacheType.SITEMAP]: <Map className="h-5 w-5" />,
  [ClientCacheType.ROBOT]: <Bot className="h-5 w-5" />,
};


export default function CacheManagementPage() {
  const {
    stats,
    clientHealth,
    isLoading,
    isClearing,
    isRevalidating,
    lastUpdated,
    fetchStats,
    checkClientHealth,
    clearCacheByType,
    clearAllCache,
    revalidateClient,
    revalidateAllClient,
    clearAllAndRevalidate,
  } = useCacheStore();

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState<{
    type: 'clearApi' | 'clearApiAll' | 'revalidate' | 'revalidateAll' | 'clearAllAndRevalidate';
    cacheType?: CacheType;
    clientType?: ClientCacheType;
    label: string;
  } | null>(null);

  // Dynamic revalidation state
  const [selectedDynamicType, setSelectedDynamicType] = useState<ClientCacheType | ''>('');
  const [slugInput, setSlugInput] = useState('');
  const [episodeSlugInput, setEpisodeSlugInput] = useState('');

  useEffect(() => {
    fetchStats();
    checkClientHealth();
  }, [fetchStats, checkClientHealth]);

  const openConfirmDialog = (action: typeof dialogAction) => {
    setDialogAction(action);
    setDialogOpen(true);
  };

  const handleConfirm = async () => {
    if (!dialogAction) return;

    setDialogOpen(false);

    switch (dialogAction.type) {
      case 'clearApi':
        if (dialogAction.cacheType) {
          await clearCacheByType(dialogAction.cacheType);
        }
        break;
      case 'clearApiAll':
        await clearAllCache();
        break;
      case 'revalidate':
        if (dialogAction.clientType) {
          await revalidateClient(dialogAction.clientType);
        }
        break;
      case 'revalidateAll':
        await revalidateAllClient();
        break;
      case 'clearAllAndRevalidate':
        await clearAllAndRevalidate();
        break;
    }

    setDialogAction(null);
  };

  const formatBytes = (bytes: string) => {
    const num = parseInt(bytes, 10);
    if (isNaN(num)) return bytes;
    if (num < 1024) return `${num} B`;
    if (num < 1024 * 1024) return `${(num / 1024).toFixed(2)} KB`;
    return `${(num / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleDynamicRevalidate = async () => {
    if (!selectedDynamicType || !slugInput.trim()) return;

    await revalidateClient(
      selectedDynamicType,
      slugInput.trim(),
      selectedDynamicType === ClientCacheType.EPISODE ? episodeSlugInput.trim() : undefined
    );

    // Reset inputs after successful revalidation
    setSlugInput('');
    setEpisodeSlugInput('');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-lg">
                <Database className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Quản lý Cache</h1>
                <p className="text-sm text-gray-500">
                  Quản lý cache API (Redis) và cache Client (Next.js)
                </p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                fetchStats();
                checkClientHealth();
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Làm mới
            </Button>
          </div>
        </div>

        {/* Statistics Overview */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tổng số Keys</CardTitle>
              <Server className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalKeys ?? 0}</div>
              <p className="text-xs text-muted-foreground">
                Cập nhật: {lastUpdated ? lastUpdated.toLocaleTimeString() : 'Chưa có'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Memory Usage</CardTitle>
              <HardDrive className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.memoryUsage?.usedMemoryHuman ?? '0B'}
              </div>
              <p className="text-xs text-muted-foreground">
                Peak: {formatBytes(stats?.memoryUsage?.usedMemoryPeak ?? '0')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Redis Version</CardTitle>
              <Database className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.redisVersion ?? 'N/A'}</div>
              <p className="text-xs text-muted-foreground">Server đang chạy</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Client Status</CardTitle>
              <Globe className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                {clientHealth?.available ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-lg font-semibold text-green-600">Sẵn sàng</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-5 w-5 text-red-500" />
                    <span className="text-lg font-semibold text-red-600">Không khả dụng</span>
                  </>
                )}
              </div>
              <p className="text-xs text-muted-foreground truncate">
                {clientHealth?.url ?? 'Chưa kiểm tra'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* API Cache Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5" />
                  Cache API (Redis)
                </CardTitle>
                <CardDescription>Quản lý cache trên server API</CardDescription>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={() =>
                  openConfirmDialog({
                    type: 'clearApiAll',
                    label: 'Tất cả cache API',
                  })
                }
                disabled={isClearing}
              >
                {isClearing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="mr-2 h-4 w-4" />
                )}
                Xóa tất cả
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {stats?.byType?.map((item) => (
                <div
                  key={item.type}
                  className="flex items-center justify-between rounded-lg border p-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                      {CACHE_TYPE_ICONS[item.type as CacheType]}
                    </div>
                    <div>
                      <p className="font-medium">{item.label}</p>
                      <p className="text-xs text-muted-foreground">{item.pattern}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={item.keyCount > 0 ? 'default' : 'secondary'}>
                      {item.keyCount} keys
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        openConfirmDialog({
                          type: 'clearApi',
                          cacheType: item.type as CacheType,
                          label: item.label,
                        })
                      }
                      disabled={isClearing || item.keyCount === 0}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Client Cache Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Cache Client (Next.js)
                </CardTitle>
                <CardDescription>
                  Revalidate cache trên website client
                  {!clientHealth?.available && (
                    <span className="ml-2 text-red-500">(Client không khả dụng)</span>
                  )}
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  openConfirmDialog({
                    type: 'revalidateAll',
                    label: 'Tất cả cache Client',
                  })
                }
                disabled={isRevalidating || !clientHealth?.available}
              >
                {isRevalidating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Revalidate tất cả
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {Object.values(ClientCacheType)
                .filter((type) => type !== ClientCacheType.ALL)
                .map((type) => (
                  <div
                    key={type}
                    className="flex items-center justify-between rounded-lg border p-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-100 text-green-600">
                        {CLIENT_CACHE_TYPE_ICONS[type]}
                      </div>
                      <div>
                        <p className="font-medium">{CLIENT_CACHE_TYPE_LABELS[type]}</p>
                        <p className="text-xs text-muted-foreground">Next.js cache</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        openConfirmDialog({
                          type: 'revalidate',
                          clientType: type,
                          label: CLIENT_CACHE_TYPE_LABELS[type],
                        })
                      }
                      disabled={isRevalidating || !clientHealth?.available}
                    >
                      <RefreshCw className="h-4 w-4 text-green-500" />
                    </Button>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        {/* Dynamic Client Revalidation Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Film className="h-5 w-5" />
              Revalidate theo Slug
            </CardTitle>
            <CardDescription>
              Revalidate cache cho các trang động (phim, bài viết, diễn viên, đạo diễn...)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4 items-end">
              <div className="space-y-2">
                <Label>Loại cache</Label>
                <Select
                  value={selectedDynamicType}
                  onValueChange={(value) => {
                    setSelectedDynamicType(value as ClientCacheType);
                    setSlugInput('');
                    setEpisodeSlugInput('');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Chọn loại cache" />
                  </SelectTrigger>
                  <SelectContent>
                    {DYNAMIC_CLIENT_CACHE_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        <div className="flex items-center gap-2">
                          {CLIENT_CACHE_TYPE_ICONS[type]}
                          <span>{CLIENT_CACHE_TYPE_LABELS[type]}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>
                  {selectedDynamicType === ClientCacheType.YEAR ? 'Năm' : 'Slug'}
                </Label>
                <Input
                  placeholder={
                    selectedDynamicType === ClientCacheType.YEAR
                      ? 'VD: 2024'
                      : selectedDynamicType === ClientCacheType.MOVIE_DETAIL
                        ? 'VD: avengers-endgame'
                        : selectedDynamicType === ClientCacheType.ACTOR
                          ? 'VD: robert-downey-jr'
                          : selectedDynamicType === ClientCacheType.DIRECTOR
                            ? 'VD: christopher-nolan'
                            : 'Nhập slug...'
                  }
                  value={slugInput}
                  onChange={(e) => setSlugInput(e.target.value)}
                  disabled={!selectedDynamicType}
                />
              </div>

              {selectedDynamicType === ClientCacheType.EPISODE && (
                <div className="space-y-2">
                  <Label>Episode Slug</Label>
                  <Input
                    placeholder="VD: tap-1"
                    value={episodeSlugInput}
                    onChange={(e) => setEpisodeSlugInput(e.target.value)}
                  />
                </div>
              )}

              <Button
                onClick={handleDynamicRevalidate}
                disabled={
                  isRevalidating ||
                  !clientHealth?.available ||
                  !selectedDynamicType ||
                  !slugInput.trim()
                }
                className="bg-green-600 hover:bg-green-700"
              >
                {isRevalidating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Revalidate
              </Button>
            </div>

            {/* Hint for users */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-600">
                <strong>Gợi ý:</strong> Sử dụng tính năng này khi bạn cần cập nhật cache cho một trang cụ thể.
                Ví dụ: sau khi chỉnh sửa thông tin phim, nhập slug của phim đó và nhấn Revalidate.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Vùng nguy hiểm
            </CardTitle>
            <CardDescription>
              Các thao tác này sẽ xóa toàn bộ cache và có thể ảnh hưởng đến hiệu suất tạm thời
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4 sm:flex-row">
              <Button
                variant="destructive"
                onClick={() =>
                  openConfirmDialog({
                    type: 'clearAllAndRevalidate',
                    label: 'Tất cả cache (API + Client)',
                  })
                }
                disabled={isClearing || isRevalidating}
                className="flex-1"
              >
                {isClearing || isRevalidating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="mr-2 h-4 w-4" />
                )}
                Xóa tất cả cache (API + Client)
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xác nhận thao tác</AlertDialogTitle>
            <AlertDialogDescription>
              Bạn có chắc chắn muốn{' '}
              {dialogAction?.type.includes('clear') ? 'xóa' : 'revalidate'}{' '}
              <strong>{dialogAction?.label}</strong>?
              {dialogAction?.type === 'clearAllAndRevalidate' && (
                <span className="block mt-2 text-red-500">
                  Thao tác này sẽ xóa toàn bộ cache trên cả server API và client. Website có thể
                  chậm hơn trong vài giây đầu tiên sau khi xóa.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hủy</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={
                dialogAction?.type.includes('clear')
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700'
              }
            >
              {dialogAction?.type.includes('clear') ? 'Xóa' : 'Revalidate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
